/**
 * Alta y edición de producto (`Producto::create`/`edit` + `agregar`/`update`).
 *
 * El formulario cambia según el tipo, como el original:
 * - **COMBUSTIBLE**: nombre abreviado, color, capacidades del tanque y los bonus.
 * - **COMBO**: sus componentes con su cantidad y su código de barras.
 * - **LUBRICANTE / OTRO**: código de barras.
 *
 * En la edición se puede además **programar el precio nuevo**, que el cron del
 * servidor aplica a su fecha (el original lo tenía en su pantalla aparte).
 *
 * Diferencia anotada: la asignación del producto al **controlador de surtidor**
 * (`pts_model::assignFuel` y `set_fuel_grades_configuration`) va contra el
 * servicio PTS2 y no se toca desde aquí.
 */

import { Input } from '@/components/ui/input';
import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { Info, Lock } from 'lucide-react';
import { ApiError } from '@/lib/http/api-error';
import { useState } from 'react';

import { hoyIso, monto } from '../gestion-formato';
import { useGuardarProducto, useProductoFicha, useProgramarPrecio } from '../productos.api';
import type { CategoriaProducto, DatosProducto, ProductoGestion } from '../gestion.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const TIPOS = ['COMBUSTIBLE', 'LUBRICANTE', 'COMBO', 'OTRO'];

interface Props {
  /** Producto a editar (`null` = alta). */
  id: number | null;
  /** `true` cuando el diálogo está abierto para un alta. */
  crear: boolean;
  onClose: () => void;
  onAviso: (mensaje: string) => void;
  categorias: CategoriaProducto[];
  productos: ProductoGestion[];
}

export function ProductoFormDialog({ id, crear, onClose, onAviso, categorias, productos }: Props) {
  const ficha = useProductoFicha(crear ? null : id);
  const guardar = useGuardarProducto(crear ? null : id);
  const programar = useProgramarPrecio();

  const [datos, setDatos] = useState<DatosProducto>(() => ({
    nombre: '',
    tipo: 'LUBRICANTE',
    precio_venta: 0,
    precio_compra: 0,
    estado: 1,
    mostrar_en_market: true,
  }));
  const [programado, setProgramado] = useState({ activo: false, precio: '', fecha: '', motivo: '' });
  const [error, setError] = useState<string | null>(null);
  const [firma, setFirma] = useState('');

  const abierto = crear || id !== null;

  // La ficha rellena el formulario en el render (guarda `firma`), sin efectos.
  if (ficha.data && !crear && firma !== String(ficha.data.producto.id)) {
    const { producto } = ficha.data;

    setFirma(String(producto.id));
    setDatos({
      nombre: producto.nombre,
      tipo: producto.tipo ?? 'LUBRICANTE',
      precio_venta: producto.precio_venta,
      precio_compra: producto.precio_compra,
      estado: producto.estado ?? 1,
      mostrar_en_market: Boolean(producto.mostrar_en_market),
      external_code_edsuite: producto.external_code_edsuite,
      codigo_contable: producto.codigo_contable,
      categoria_id: producto.categoria_id,
      subcategoria_id: producto.subcategoria_id,
      nombre_abreviado: producto.nombre_abreviado,
      color: producto.color,
      capacidad_maxima_tanque: producto.capacidad_maxima_tanque,
      capacidad_minima_tanque: producto.capacidad_minima_tanque,
      bonus_code: producto.bonus_code,
      bonus_description: producto.bonus_description,
      barcode: producto.barcode,
      combo_items: producto.combo_items ?? [],
    });
    setError(null);
  }

  const cambiar = (parcial: Partial<DatosProducto>) => setDatos((previo) => ({ ...previo, ...parcial }));

  const subcategorias = (categorias.find((item) => item.id === datos.categoria_id)?.subcategorias ?? []);

  /** Guarda el producto y, si se pidió, programa su precio nuevo. */
  const enviar = () => {
    setError(null);
    guardar.mutate(
      {
        ...datos,
        categoria_id: datos.categoria_id || null,
        subcategoria_id: datos.subcategoria_id || null,
      },
      {
        onSuccess: async (respuesta) => {
          const productoId = (respuesta as { id?: number }).id ?? id;

          if (programado.activo && productoId) {
            await programar.mutateAsync({
              producto_id: productoId,
              precio_venta_nuevo: Number(programado.precio),
              fecha_programada: programado.fecha,
              motivo: programado.motivo.trim() || undefined,
            });
          }

          onAviso(
            programado.activo
              ? `${respuesta.mensaje} Con el precio programado para el ${programado.fecha}.`
              : respuesta.mensaje,
          );
          onClose();
        },
        onError: (fallo) =>
          setError(fallo instanceof ApiError ? fallo.message : 'No se pudo guardar el producto.'),
      },
    );
  };

  return (
    <Dialog
      open={abierto}
      onClose={onClose}
      size="xl"
      title={crear ? 'Nuevo producto' : `Editar producto${datos.nombre ? ` · ${datos.nombre}` : ''}`}
      description={
        crear
          ? 'El original guardaba el producto según su tipo; las categorías y subcategorías se pueden crear desde «Categorías».'
          : 'Al cambiar el precio de venta se registra en el historial y se ajusta el descuento de los créditos por precio fijo del producto.'
      }
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button size="sm" loading={guardar.isPending || programar.isPending} onClick={enviar}>
            {crear ? 'Crear producto' : 'Guardar cambios'}
          </Button>
        </>
      }
    >
      {!crear && ficha.isLoading ? <Skeleton className="h-72 w-full" /> : null}

      {!crear && ficha.isError ? (
        <Alert variant="error" title="No se pudo leer el producto">
          {ficha.error instanceof ApiError ? ficha.error.message : 'Inténtalo de nuevo.'}
        </Alert>
      ) : null}

      {(crear || ficha.data) && (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Input
              label="Nombre"
              value={datos.nombre}
              onChange={(evento) => cambiar({ nombre: evento.target.value })}
            />
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Tipo</span>
              <Select
                className={CONTROL}
                value={datos.tipo}
                onChange={(evento) => cambiar({ tipo: evento.target.value })}
              >
                {TIPOS.map((tipo) => (
                  <option key={tipo} value={tipo}>
                    {tipo}
                  </option>
                ))}
              </Select>
            </label>
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Estado</span>
              <Select
                className={CONTROL}
                value={datos.estado}
                onChange={(evento) => cambiar({ estado: Number(evento.target.value) })}
              >
                <option value={1}>Activo</option>
                <option value={0}>Inactivo</option>
              </Select>
            </label>

            <Input
              label="Precio de venta (S/)"
              type="number"
              step="0.001"
              value={datos.precio_venta}
              onChange={(evento) => cambiar({ precio_venta: Number(evento.target.value) })}
            />
            <Input
              label="Precio de compra (S/)"
              type="number"
              step="0.001"
              value={datos.precio_compra}
              onChange={(evento) => cambiar({ precio_compra: Number(evento.target.value) })}
            />
            <Input
              label="Código de barras"
              value={datos.barcode ?? ''}
              onChange={(evento) => cambiar({ barcode: evento.target.value })}
            />

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Categoría</span>
              <Select
                className={CONTROL}
                value={datos.categoria_id ?? ''}
                onChange={(evento) =>
                  cambiar({
                    categoria_id: evento.target.value ? Number(evento.target.value) : null,
                    subcategoria_id: null,
                  })
                }
              >
                <option value="">Sin categoría</option>
                {categorias.map((categoria) => (
                  <option key={categoria.id} value={categoria.id}>
                    {categoria.nombre}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Subcategoría</span>
              <Select
                className={CONTROL}
                disabled={!subcategorias.length}
                value={datos.subcategoria_id ?? ''}
                onChange={(evento) =>
                  cambiar({ subcategoria_id: evento.target.value ? Number(evento.target.value) : null })
                }
              >
                <option value="">Sin subcategoría</option>
                {subcategorias.map((sub) => (
                  <option key={sub.id} value={sub.id}>
                    {sub.nombre}
                  </option>
                ))}
              </Select>
            </label>

            <Input
              label="Código contable"
              value={datos.codigo_contable ?? ''}
              onChange={(evento) => cambiar({ codigo_contable: evento.target.value })}
            />
            <Input
              label="Código externo de EDSuite"
              value={datos.external_code_edsuite ?? ''}
              onChange={(evento) => cambiar({ external_code_edsuite: evento.target.value })}
            />

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-4 accent-brand-500"
                checked={datos.mostrar_en_market}
                onChange={(evento) => cambiar({ mostrar_en_market: evento.target.checked })}
              />
              <span className="font-medium text-ink-700">Mostrar en el market</span>
            </label>
          </div>

          {datos.tipo === 'COMBUSTIBLE' ? (
            <section className="rounded-card border border-ink-100 bg-white p-4">
              <h3 className="mb-3 text-sm font-semibold text-ink-800">Datos del combustible</h3>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <Input
                  label="Nombre abreviado"
                  value={datos.nombre_abreviado ?? ''}
                  onChange={(evento) => cambiar({ nombre_abreviado: evento.target.value })}
                />
                <Input
                  label="Color (hex)"
                  placeholder="#ff0000"
                  value={datos.color ?? ''}
                  onChange={(evento) => cambiar({ color: evento.target.value })}
                />
                <Input
                  label="Capacidad máxima del tanque (GLL)"
                  type="number"
                  step="0.001"
                  value={datos.capacidad_maxima_tanque ?? 0}
                  onChange={(evento) => cambiar({ capacidad_maxima_tanque: Number(evento.target.value) })}
                />
                <Input
                  label="Capacidad mínima del tanque (GLL)"
                  type="number"
                  step="0.001"
                  value={datos.capacidad_minima_tanque ?? 0}
                  onChange={(evento) => cambiar({ capacidad_minima_tanque: Number(evento.target.value) })}
                />
                <Input
                  label="Bonus code"
                  value={datos.bonus_code ?? ''}
                  onChange={(evento) => cambiar({ bonus_code: evento.target.value })}
                />
                <Input
                  label="Bonus (descripción)"
                  value={datos.bonus_description ?? ''}
                  onChange={(evento) => cambiar({ bonus_description: evento.target.value })}
                />
              </div>

              <Alert className="mt-3" variant="info" title="Controlador del surtidor">
                <span className={ICONO_EN_ALERTA}>
                  <Lock className="size-4" aria-hidden="true" />
                  La asignación del producto a un correlativo del controlador (`assignFuel`) no se hace
                  desde aquí: la hace el servicio del surtidor.
                </span>
              </Alert>
            </section>
          ) : null}

          {datos.tipo === 'COMBO' ? (
            <section className="rounded-card border border-ink-100 bg-white p-4">
              <h3 className="mb-3 text-sm font-semibold text-ink-800">Componentes del combo</h3>

              <div className="space-y-2">
                {(datos.combo_items ?? []).map((item, indice) => (
                  <div key={`${item.producto_id}-${indice}`} className="flex flex-wrap items-end gap-2">
                    <label className="flex min-w-[240px] flex-1 flex-col gap-1.5 text-sm">
                      <span className="font-medium text-ink-700">Producto</span>
                      <Select
                        className={CONTROL}
                        value={item.producto_id}
                        onChange={(evento) => {
                          const copia = [...(datos.combo_items ?? [])];
                          copia[indice] = { ...item, producto_id: Number(evento.target.value) };
                          cambiar({ combo_items: copia });
                        }}
                      >
                        <option value={0}>SELECCIONE</option>
                        {productos.map((producto) => (
                          <option key={producto.id} value={producto.id}>
                            {producto.nombre}
                          </option>
                        ))}
                      </Select>
                    </label>

                    <Input
                      label="Cantidad"
                      type="number"
                      step="0.001"
                      containerClassName="w-32"
                      value={item.cantidad}
                      onChange={(evento) => {
                        const copia = [...(datos.combo_items ?? [])];
                        copia[indice] = { ...item, cantidad: Number(evento.target.value) };
                        cambiar({ combo_items: copia });
                      }}
                    />

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const copia = (datos.combo_items ?? []).filter((_, posicion) => posicion !== indice);
                        cambiar({ combo_items: copia });
                      }}
                    >
                      Quitar
                    </Button>
                  </div>
                ))}
              </div>

              <Button
                className="mt-3"
                variant="outline"
                size="sm"
                onClick={() =>
                  cambiar({
                    combo_items: [...(datos.combo_items ?? []), { producto_id: 0, cantidad: 1 }],
                  })
                }
              >
                Añadir componente
              </Button>
            </section>
          ) : null}

          <section className="rounded-card border border-ink-100 bg-white p-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-4 accent-brand-500"
                checked={programado.activo}
                onChange={(evento) => setProgramado((previo) => ({ ...previo, activo: evento.target.checked }))}
              />
              <span className="font-medium text-ink-700">Programar el precio nuevo</span>
            </label>

            {programado.activo ? (
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <Input
                  label="Precio de venta nuevo (S/)"
                  type="number"
                  step="0.001"
                  value={programado.precio}
                  onChange={(evento) => setProgramado((previo) => ({ ...previo, precio: evento.target.value }))}
                />
                <Input
                  label="Fecha y hora"
                  type="datetime-local"
                  min={hoyIso()}
                  value={programado.fecha}
                  onChange={(evento) => setProgramado((previo) => ({ ...previo, fecha: evento.target.value }))}
                />
                <Input
                  label="Motivo (opcional)"
                  value={programado.motivo}
                  onChange={(evento) => setProgramado((previo) => ({ ...previo, motivo: evento.target.value }))}
                />
                <p className="text-xs text-ink-500 sm:col-span-3">
                  El precio programado queda pendiente y lo aplica el cron del servidor en la fecha
                  indicada. Precio actual: {monto(datos.precio_venta)}.
                </p>
              </div>
            ) : null}
          </section>

          {!crear && ficha.data?.programados.length ? (
            <Alert variant="info" title="Precios ya programados">
              <span className={ICONO_EN_ALERTA}>
                <Info className="size-4" aria-hidden="true" />
                {ficha.data.programados
                  .map((item) => `${monto(item.precio_venta_nuevo)} el ${item.fecha_programada ?? ''}`)
                  .join(' · ')}
              </span>
            </Alert>
          ) : null}

          {error ? (
            <Alert variant="error" title="No se pudo guardar">
              {error}
            </Alert>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}
